import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import './ResetShared.css';

const { Title, Text } = Typography;

interface ResetFormData {
  new_password: string;
  confirm_new_password: string;
}

const ResetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { token, email: encodedEmail } = useParams<{ token: string; email: string }>();
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<{ username: string; full_name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token && encodedEmail) {
      verifyToken();
    } else {
      setError('Invalid reset link.');
      setVerifyLoading(false);
    }
  }, [token, encodedEmail]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`/v1/auth/token-verify?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid or expired token.');
      }
    } catch (err) {
      setError('Failed to verify token.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const onFinish = async (values: ResetFormData) => {
    if (values.new_password !== values.confirm_new_password) {
      message.error('Passwords do not match.');
      return;
    }

    if (!token || !encodedEmail) {
      message.error('Invalid reset link.');
      return;
    }

    const decodedEmail = atob(encodedEmail);

    setLoading(true);
    try {
      const response = await fetch('/v1/auth/change-forgot-password/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          email_or_username: decodedEmail,
          new_password: values.new_password,
          confirm_new_password: values.confirm_new_password,
        }),
      });

      if (response.ok) {
        message.success('Password reset successfully. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Failed to reset password.');
      }
    } catch (err) {
      message.error('Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  if (verifyLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !userInfo) {
    return (
      <div className="reset-container">
        <div className="reset-content">
          <Card className="reset-card">
            <Title level={2}>Password Reset</Title>
            <Alert message={error || 'Invalid reset link.'} type="error" showIcon />
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Button type="primary" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-container">
      <div className="reset-content">
        <Card className="reset-card">
          <div className="reset-header">
            <Title level={2}>Reset Password</Title>
            <Text type="secondary">Welcome back, {userInfo.full_name || userInfo.username}!</Text>
          </div>

          <Form
            form={form}
            name="reset"
            onFinish={onFinish}
            layout="vertical"
            size="large"
            className="reset-form"
          >
            <Form.Item
              name="new_password"
              label="New Password"
              rules={[
                { required: true, message: 'Please input your new password!' },
                { min: 8, message: 'Password must be at least 8 characters long.' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_new_password"
              label="Confirm New Password"
              rules={[
                { required: true, message: 'Please confirm your new password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="reset-button"
                loading={loading}
                block
              >
                {loading ? <Spin size="small" /> : 'Reset Password'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
